import { useState, useEffect } from 'react';
import { getMoncoUploads } from '../api/endpoints';
import { ConfigProvider, useConfig } from './contexts/ConfigContext';
import { LogProvider, useLog } from './contexts/LogContext';
import { ToolTabs } from './components/common/ToolTabs';
import { ModeToggle } from './components/common/ModeToggle';
import { ConfigSection } from './components/ConfigSection';
import { LogPanel } from './components/common/LogPanel';
import { FloorPlansContainer } from './containers/FloorPlansContainer';
import { EditFloorPlanContainer } from './containers/EditFloorPlanContainer';
import { ImportLayoutContainer } from './containers/ImportLayoutContainer';
import { DiagramsContainer } from './containers/DiagramsContainer';
import { DeleteDistributionBoardFileContainer } from './containers/DeleteDistributionBoardFileContainer';
import { CompressionTest } from './components/CompressionTest';
import { CreditsContainer } from './containers/CreditsContainer';
import { SendEmailContainer } from './containers/SendEmailContainer';
import type { ToolType } from './components/common/ToolTabs';

type FloorPlanMode = 'create' | 'edit' | 'layout';

const FLOOR_PLAN_MODE_OPTIONS = [
  { value: 'create', label: 'Create New Floor Plan' },
  { value: 'edit', label: 'Edit Floor Plan' },
  { value: 'layout', label: 'Import Layout' },
];

const FLOOR_PLAN_MODE_HINTS: Record<FloorPlanMode, string> = {
  create:
    'Will create a new floor plan (using plan_name from counts JSON), then import pages, counts, and routes.',
  edit:
    'Search by project: delete a floor plan, edit the project status, or edit per-upload statuses.',
  layout:
    'Import a layout JSON (zones, rooms, walls, doors, windows) into one page of an existing floor plan. Replaces what that page has.',
};

const DIAGRAM_MODE_OPTIONS = [
  { value: 'import', label: 'Import Diagram' },
  { value: 'delete', label: 'Delete Distribution Board File' },
];

const DIAGRAM_MODE_HINTS: Record<string, string> = {
  import:
    'Will create a new distribution board file, then import all diagram data (diagrams, pages, item types, occurrences) from a single JSON file.',
  delete:
    'Search for an existing distribution board file by project, then select and delete it.',
};

const AppContent = () => {
  const { projectId } = useConfig();
  const { logs } = useLog();

  const [currentTool, setCurrentTool] = useState<ToolType>('floorplans');
  const [floorPlanMode, setFloorPlanMode] = useState<FloorPlanMode>('create');
  const [diagramMode, setDiagramMode] = useState<'import' | 'delete'>('import');

  return (
    <div className="bg-[#0d1117] text-[#c9d1d9] min-h-screen p-6 font-mono text-sm">
      <h1 className="text-xl font-bold text-[#c9d1d9] mb-4">
        Smart Surveyor Upload Tool
      </h1>
      <ToolTabs currentTool={currentTool} onToolChange={setCurrentTool} />
      <ConfigSection />
      {currentTool === 'floorplans' && (
        <div>
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
            <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
              Mode
            </h2>
            <ModeToggle
              options={FLOOR_PLAN_MODE_OPTIONS}
              activeValue={floorPlanMode}
              onChange={(v) => setFloorPlanMode(v as FloorPlanMode)}
              hint={FLOOR_PLAN_MODE_HINTS[floorPlanMode]}
            />
          </div>
          {floorPlanMode === 'create' && (
            <FloorPlansContainer projectId={projectId} />
          )}
          {floorPlanMode === 'edit' && <EditFloorPlanContainer />}
          {floorPlanMode === 'layout' && (
            <ImportLayoutContainer projectId={projectId} />
          )}
        </div>
      )}
      {currentTool === 'diagrams' && (
        <div>
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
            <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
              Mode
            </h2>
            <ModeToggle
              options={DIAGRAM_MODE_OPTIONS}
              activeValue={diagramMode}
              onChange={(v) => setDiagramMode(v as 'import' | 'delete')}
              hint={DIAGRAM_MODE_HINTS[diagramMode]}
            />
          </div>
          {diagramMode === 'import' && (
            <DiagramsContainer projectId={projectId} />
          )}
          {diagramMode === 'delete' && <DeleteDistributionBoardFileContainer />}
        </div>
      )}
      {currentTool === 'credits' && <CreditsContainer />}
      {currentTool === 'compression' && <CompressionTest />}
      {currentTool === 'sendemail' && <SendEmailContainer />}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Log
        </h2>
        <LogPanel logs={logs} />
      </div>
    </div>
  );
};

export const AdminApp = () => {
  const [moncoProjectId, setMoncoProjectId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    let cancelled = false;
    getMoncoUploads().then((result) => {
      if (cancelled) return;
      if (result.ok && result.data && result.data.monco_project_id != null) {
        setMoncoProjectId(String(result.data.monco_project_id));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ConfigProvider activeProjectId={moncoProjectId}>
      <LogProvider>
        <AppContent />
      </LogProvider>
    </ConfigProvider>
  );
};
